// SPDX-License-Identifier: Elastic-2.0
/**
 * ThemeSection — Settings section for theme preference (T-028).
 *
 * Three radio buttons: Dark (default) · Light · Auto. Each radio change
 * synchronously calls `useTheme.setTheme()`, which persists to
 * localStorage and flips `<html data-theme>` (via ThemeProvider's
 * effect). No save button — every interaction is immediate, matching
 * the in-app theme pill behaviour from T-016.
 */
import { useTranslation } from "react-i18next";

import { Card } from "@/components/Card/Card";
import { useTheme } from "@/theme/useTheme";
import type { ThemeMode } from "@/theme/useTheme";

import styles from "./ThemeSection.module.css";

const OPTIONS: ReadonlyArray<{ value: ThemeMode; labelKey: string; hintKey?: string }> = [
  { value: "dark", labelKey: "settings.theme.dark" },
  { value: "light", labelKey: "settings.theme.light" },
  { value: "auto", labelKey: "settings.theme.auto", hintKey: "settings.theme.auto_hint" },
];

export function ThemeSection(): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme((s) => s.theme);
  const setTheme = useTheme((s) => s.setTheme);

  return (
    <Card as="section" variant="elevated" padding="md" data-testid="settings-theme">
      <h2 className={styles.title}>{t("settings.theme.section_title")}</h2>
      <div
        role="radiogroup"
        aria-label={t("settings.theme.section_title")}
        className={styles.group}
      >
        {OPTIONS.map((opt) => {
          const id = `settings-theme-${opt.value}`;
          const checked = theme === opt.value;
          return (
            <label
              key={opt.value}
              htmlFor={id}
              className={styles.option}
              data-checked={checked ? "true" : undefined}
              data-testid={`settings-theme-option-${opt.value}`}
            >
              <input
                id={id}
                type="radio"
                name="settings-theme"
                value={opt.value}
                checked={checked}
                onChange={() => setTheme(opt.value)}
                className={styles.radio}
              />
              <span className={styles.optionText}>
                <span className={styles.optionLabel}>{t(opt.labelKey)}</span>
                {opt.hintKey ? (
                  <span className={styles.optionHint}>{t(opt.hintKey)}</span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
    </Card>
  );
}

export default ThemeSection;
